// The debug stream, as a structure rather than a list of lines.
//
// Stories VC-014 and RO-021. `agent_runtime_lld.md` §30.7 (the frame),
// §30.1–§30.3 and §30.10 (the families), §30.13 (what a reader derives).
//
// # No DOM here, deliberately
//
// Everything with a decision in it — which frames pair into a span, what a
// span's duration is, which agent is whose child — lives in this file, and
// this file imports nothing. That is what lets `model.test.mjs` assert it in
// Node against a stream captured from the running binary. The three views
// render what this returns and decide nothing themselves.
//
// # Durations come from the stream, never from the clock
//
// A completed pass carries `duration_ms` and a completed tool carries its
// own; the panel never subtracts two timestamps to invent one. The two
// answers differ — the stream's is the work, the subtraction includes
// whatever queuing sat between the frames — and only one of them is the
// number §30 recorded.

/** Frames whose `started` opens a span, by the envelope id that pairs them. */
const SPAN_KINDS = {
  "inference.started": { kind: "inference", key: "inference_pass_id" },
  "tool.started": { kind: "tool", key: "invocation_id" },
  "rehydration.requested": { kind: "rehydration", key: "agent_id" },
  "history.load_started": { kind: "history", key: "agent_id" },
  "child_agent.spawn_requested": { kind: "child", key: "invocation_id" },
};

/** How each span ends, and whether the ending is a failure. */
const SPAN_ENDS = {
  "inference.completed": { kind: "inference", key: "inference_pass_id", ok: true },
  "inference.failed": { kind: "inference", key: "inference_pass_id", ok: false },
  "tool.completed": { kind: "tool", key: "invocation_id", ok: true },
  "tool.failed": { kind: "tool", key: "invocation_id", ok: false },
  "rehydration.activated": { kind: "rehydration", key: "agent_id", ok: true },
  "rehydration.failed": { kind: "rehydration", key: "agent_id", ok: false },
  "history.loaded": { kind: "history", key: "agent_id", ok: true },
  "child_agent.spawned": { kind: "child", key: "invocation_id", ok: true },
};

/** How many `--agent-N` tokens the theme defines (§30.13.6). */
export const AGENT_COLOURS = 8;

/** Milliseconds since the epoch, or `undefined` for a frame without a time. */
function at(frame) {
  const ms = Date.parse(frame.timestamp ?? "");
  return Number.isFinite(ms) ? ms : undefined;
}

/**
 * One frame, parsed.
 *
 * A `gap` is kept rather than dropped: §30 makes this plane sampled and
 * droppable (`AGENTS.md` §I.3.15), and a viewer that hid its own losses
 * would be drawing a timeline it cannot support.
 */
export function parseFrame(line) {
  const frame = typeof line === "string" ? JSON.parse(line) : line;
  return { ...frame, at: at(frame) };
}

/**
 * Everything the three views need, from the frames received so far.
 *
 * Rebuilt from the whole list rather than updated incrementally. The stream
 * is bounded by a session and a reader's patience; an incremental builder
 * would be a second place for the pairing rules to live, and the pairing
 * rules are the part worth getting right.
 */
export function buildModel(frames) {
  const agents = new Map();
  const spans = [];
  const open = new Map();
  const responses = new Map();
  const turns = new Map();
  const gaps = [];

  const agentOf = (id) => {
    if (!id) return undefined;
    if (!agents.has(id)) {
      // A frame may name an agent before `agent.created` arrives — the
      // stream is sampled, and a resumed reader starts mid-session. The
      // placeholder is filled in if the creation turns up.
      // The colour is the order of first appearance (§30.13.1), so it never
      // moves as the stream grows, and a parent — which names its child in
      // `child_agent.spawned` before the child says anything — always has
      // a lower one than its children.
      agents.set(id, {
        id,
        parent: null,
        definition: undefined,
        version: undefined,
        children: [],
        colour: agents.size % AGENT_COLOURS,
      });
    }
    return agents.get(id);
  };

  for (const frame of frames) {
    if (frame.frame === "gap") {
      gaps.push({ missed: frame.missed ?? 0, after: gaps.length ? undefined : 0, index: frames.indexOf(frame) });
      continue;
    }
    const agent = agentOf(frame.agent_id);
    const payload = frame.payload ?? {};

    if (frame.event_type === "agent.created" && agent) {
      agent.definition = payload.definition;
      agent.version = payload.version;
      agent.parent = payload.parent ?? null;
    }

    if (frame.turn_id && !turns.has(frame.turn_id)) {
      turns.set(frame.turn_id, { id: frame.turn_id, opened: frame.at, closed: undefined, state: undefined });
    }
    if (frame.event_type === "session.turn_closed" && frame.turn_id) {
      const turn = turns.get(frame.turn_id);
      turn.closed = frame.at;
      turn.state = payload.state;
    }

    // A response is its own span: §27.3's lifecycle is per response, and the
    // question a latency complaint starts from — how long until the first
    // partial, how long until the final — is answered by this one and no
    // other.
    if (frame.event_type === "response.started") {
      responses.set(payload.response_id, {
        kind: "response",
        id: payload.response_id,
        agent_id: frame.agent_id,
        turn_id: frame.turn_id,
        invocation_id: frame.invocation_id,
        phase: payload.phase,
        start: frame.at,
        end: undefined,
        ok: true,
        firstPartial: undefined,
        finalAt: undefined,
        revisions: 0,
        frames: [frame],
      });
      continue;
    }
    if (frame.event_type === "response.projection_updated") {
      const response = responses.get(payload.response_id);
      if (response) {
        response.revisions = Math.max(response.revisions, payload.revision ?? 0);
        response.firstPartial ??= frame.at;
        response.frames.push(frame);
      }
      continue;
    }
    if (
      frame.event_type === "response.completed" ||
      frame.event_type === "response.failed" ||
      frame.event_type === "response.cancelled"
    ) {
      const response = responses.get(payload.response_id);
      if (response) {
        response.end = frame.at;
        response.finalAt = frame.at;
        response.ok = frame.event_type === "response.completed";
        response.frames.push(frame);
      }
      continue;
    }

    const opens = SPAN_KINDS[frame.event_type];
    if (opens) {
      const key = `${opens.kind}:${frame[opens.key] ?? payload[opens.key] ?? ""}`;
      const span = {
        kind: opens.kind,
        id: frame[opens.key] ?? payload[opens.key],
        agent_id: frame.agent_id,
        turn_id: frame.turn_id,
        start: frame.at,
        end: undefined,
        duration_ms: undefined,
        ok: undefined,
        label: labelFor(opens.kind, payload),
        frames: [frame],
      };
      open.set(key, span);
      spans.push(span);
      continue;
    }

    const ends = SPAN_ENDS[frame.event_type];
    if (ends) {
      const key = `${ends.kind}:${frame[ends.key] ?? payload[ends.key] ?? ""}`;
      const span = open.get(key);
      if (span) {
        span.end = frame.at;
        span.ok = ends.ok;
        // The stream's own number where it has one. `duration_ms` is the
        // work; the difference of two timestamps is the work plus whatever
        // sat between the frames.
        span.duration_ms =
          typeof payload.duration_ms === "number"
            ? payload.duration_ms
            : span.start !== undefined && frame.at !== undefined
              ? frame.at - span.start
              : undefined;
        span.frames.push(frame);
        open.delete(key);
      }
      // A `child_agent.spawned` names the new instance, which is how a
      // child's own frames find a parent before its `agent.created` does.
      if (frame.event_type === "child_agent.spawned" && payload.agent_id) {
        const child = agentOf(payload.agent_id);
        if (child.parent === null && frame.agent_id) {
          child.parent = frame.agent_id;
        }
      }
      continue;
    }
  }

  // A span still open at the end of what we received. Drawn to the edge
  // rather than dropped: "the tool never returned" is the answer somebody is
  // looking for, and a row that vanished would look like it never started.
  const unfinished = spans.filter((span) => span.end === undefined);

  for (const agent of agents.values()) {
    agent.children = [...agents.values()].filter((other) => other.parent === agent.id).map((other) => other.id);
  }

  const all = [...spans, ...responses.values()];
  const times = [
    ...frames.map((frame) => frame.at),
    ...all.map((span) => span.end),
  ].filter((time) => time !== undefined);

  return {
    agents,
    turns,
    spans: all,
    unfinished,
    gaps,
    roots: [...agents.values()].filter((agent) => !agent.parent || !agents.has(agent.parent)).map((a) => a.id),
    start: times.length ? Math.min(...times) : undefined,
    end: times.length ? Math.max(...times) : undefined,
    frames,
  };
}

/** What a span is called in a waterfall row. */
function labelFor(kind, payload) {
  switch (kind) {
    case "inference":
      return payload.model ? `${payload.provider ?? "model"} · ${payload.model}` : "inference";
    case "tool":
      return payload.tool ?? "tool";
    case "history":
      return payload.mode ? `history · ${payload.mode}` : "history";
    case "rehydration":
      return "rehydration";
    case "child":
      return payload.definition ? `spawn · ${payload.definition}` : "spawn";
    default:
      return kind;
  }
}

/**
 * Spans grouped by agent, parents before children (§33.1's order, for the
 * same reason: a reader follows a hierarchy downwards).
 *
 * Depth is carried so a renderer can indent without walking the tree again.
 */
export function spansByAgent(model) {
  const rows = [];
  const visit = (agentId, depth) => {
    const agent = model.agents.get(agentId);
    if (!agent) return;
    const mine = model.spans
      .filter((span) => span.agent_id === agentId)
      .sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
    rows.push({ agent, depth, spans: mine });
    for (const child of agent.children) {
      visit(child, depth + 1);
    }
  };
  for (const root of model.roots) {
    visit(root, 0);
  }
  // An agent whose creation never arrived and whose parent is not in this
  // stream. Shown rather than hidden: its frames are real.
  for (const agent of model.agents.values()) {
    if (!rows.some((row) => row.agent.id === agent.id)) {
      rows.push({ agent, depth: 0, spans: model.spans.filter((span) => span.agent_id === agent.id) });
    }
  }
  return rows;
}

/**
 * The milliseconds between the session's first frame and `at` (§30.13.1).
 * `undefined` when either is unknown, which a view draws as nothing.
 */
export function since(model, at) {
  return model.start === undefined || at === undefined ? undefined : at - model.start;
}

/** `+340 ms`, `+1.2 s`, `+12 s`, `+1:05` — §30.13.1's one format. */
export function delta(ms) {
  if (ms === undefined || ms === null || !Number.isFinite(ms)) return "";
  return `+${elapsed(ms)}`;
}

/** A duration in the same format, unsigned. */
export function elapsed(ms) {
  if (ms === undefined || ms === null || !Number.isFinite(ms)) return "";
  const abs = Math.max(0, ms);
  if (abs < 1000) return `${Math.round(abs)} ms`;
  if (abs < 10_000) return `${(abs / 1000).toFixed(1)} s`;
  if (abs < 60_000) return `${Math.round(abs / 1000)} s`;
  const total = Math.round(abs / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** What an agent is called on its tag: `definition · short id`. */
export function agentLabel(agent) {
  if (!agent) return "";
  return agent.definition ? `${agent.definition} · ${short(agent.id)}` : short(agent.id);
}

/** Which family an item's kind is coloured and iconed as (§30.13.6). */
export const FAMILY_OF_ITEM = {
  turn: "session",
  message: "message",
  inference: "inference",
  tool: "tool",
  response: "response",
  child: "child_agent",
  error: "error",
};

/** Which item an `action.routed` target opens. The rest stay frames. */
const ITEM_OF_TARGET = { tool: "tool", response: "response", spawn_agent: "child" };

/**
 * The timeline's cards: one per unit of work (§30.13.1's table).
 *
 * Built in arrival order from the same frames as `buildModel`, and returned
 * with the frames no item took, which the Events and Tree views still show.
 */
export function buildItems(model) {
  const items = [];
  const consumed = new Set();
  const byInvocation = new Map();
  const byPass = new Map();
  const byTurn = new Map();
  const childByAgent = new Map();
  // The latest pass on each agent to complete, and how many actions it may
  // still own (§30.13.1's rule).
  const lastPass = new Map();
  // The pass running on each agent, between its start and its end.
  const runningPass = new Map();
  // The turn open on each agent, for the frames that carry no `turn_id`.
  const openTurn = new Map();

  // §30.13.1's rule, applied once, at an item's first frame: the pass still
  // running on the agent; else the latest completed, up to the actions it
  // said it emitted; else none.
  const attach = (item, frame) => {
    if (item.attached) return;
    item.attached = true;
    const running = runningPass.get(frame.agent_id);
    if (running) {
      item.passId = running;
      return;
    }
    const owner = lastPass.get(frame.agent_id);
    if (owner && owner.remaining > 0) {
      item.passId = owner.item.id.slice("inference:".length);
      owner.remaining -= 1;
    }
  };

  // Idempotent: a terminal frame is both "a frame of this item" and "the
  // frame that ends it", and is one frame.
  const take = (item, frame) => {
    if (!item.frames.includes(frame)) item.frames.push(frame);
    consumed.add(frame);
    if (frame.at !== undefined) {
      if (item.start === undefined || frame.at < item.start) item.start = frame.at;
    }
  };
  const open = (kind, id, frame, extra = {}) => {
    const item = {
      id: `${kind}:${id}`,
      kind,
      family: FAMILY_OF_ITEM[kind],
      agentId: frame.agent_id ?? null,
      turnId: frame.turn_id ?? openTurn.get(frame.agent_id) ?? null,
      start: frame.at,
      end: undefined,
      durationMs: undefined,
      state: "running",
      passId: null,
      frames: [],
      ...extra,
    };
    items.push(item);
    take(item, frame);
    return item;
  };
  const finish = (item, frame, state, payload) => {
    take(item, frame);
    item.end = frame.at;
    item.state = state;
    // The stream's number where it has one, never a subtraction (§30.1).
    if (typeof payload?.duration_ms === "number") item.durationMs = payload.duration_ms;
  };
  const instant = (item) => {
    item.end = item.start;
    item.state = "ok";
  };
  const invocationItem = (kind, frame) => {
    const id = frame.invocation_id;
    if (!id) return undefined;
    let item = byInvocation.get(id);
    if (!item) {
      item = open(kind, id, frame);
      attach(item, frame);
      byInvocation.set(id, item);
    } else if (item.kind === "pending") {
      item.kind = kind;
      item.family = FAMILY_OF_ITEM[kind];
      item.id = `${kind}:${id}`;
      items.push(item);
    }
    return item;
  };

  for (const frame of model.frames) {
    if (frame.frame === "gap") continue;
    const payload = frame.payload ?? {};
    const type = frame.event_type;

    if (type === "session.turn_opened" && frame.turn_id) {
      const item = open("turn", frame.turn_id, frame, { index: payload.turn_index, trigger: payload.trigger });
      item.turnId = frame.turn_id;
      byTurn.set(frame.turn_id, item);
      if (frame.agent_id) openTurn.set(frame.agent_id, frame.turn_id);
      continue;
    }
    if (type === "session.turn_closed" && frame.turn_id) {
      const item = byTurn.get(frame.turn_id);
      if (item) {
        const state = payload.state === "completed" ? "ok" : payload.state === "cancelled" ? "cancelled" : payload.state ? "failed" : "ok";
        finish(item, frame, state, payload);
        item.closedAs = payload.state;
      }
      if (frame.agent_id && openTurn.get(frame.agent_id) === frame.turn_id) openTurn.delete(frame.agent_id);
      continue;
    }
    if (type === "message.accepted") {
      const item = open("message", payload.message_id ?? frame.sequence, frame, {
        sender: payload.sender,
        priority: payload.priority,
        duplicate: payload.duplicate === true,
      });
      instant(item);
      continue;
    }
    if (type === "inference.started" && frame.inference_pass_id) {
      const item = open("inference", frame.inference_pass_id, frame, {
        provider: payload.provider,
        model: payload.model,
        attempt: payload.attempt,
      });
      byPass.set(frame.inference_pass_id, item);
      if (frame.agent_id) runningPass.set(frame.agent_id, frame.inference_pass_id);
      continue;
    }
    if ((type === "inference.completed" || type === "inference.failed") && frame.inference_pass_id) {
      const item = byPass.get(frame.inference_pass_id) ?? open("inference", frame.inference_pass_id, frame);
      finish(item, frame, type === "inference.completed" ? "ok" : "failed", payload);
      item.model ??= payload.model;
      item.provider ??= payload.provider;
      item.usage = payload.usage;
      item.stopReason = payload.stop_reason;
      item.emittedActions = payload.emitted_actions;
      if (frame.agent_id && runningPass.get(frame.agent_id) === frame.inference_pass_id) {
        runningPass.delete(frame.agent_id);
      }
      if (frame.agent_id) {
        lastPass.set(frame.agent_id, {
          item,
          remaining: typeof payload.emitted_actions === "number" ? payload.emitted_actions : Infinity,
        });
      }
      continue;
    }
    if (type === "action.received" && frame.invocation_id) {
      let item = byInvocation.get(frame.invocation_id);
      if (!item) {
        // Not an item yet: the routing decides which kind. Held here until
        // it does, and dropped back to a plain frame if it never does.
        item = {
          id: `pending:${frame.invocation_id}`,
          kind: "pending",
          agentId: frame.agent_id ?? null,
          turnId: frame.turn_id ?? openTurn.get(frame.agent_id) ?? null,
          start: frame.at,
          end: undefined,
          durationMs: undefined,
          state: "running",
          passId: null,
          frames: [],
        };
        byInvocation.set(frame.invocation_id, item);
      }
      attach(item, frame);
      item.action = payload.action;
      item.frames.push(frame);
      if (item.kind !== "pending") consumed.add(frame);
      continue;
    }
    if (type === "action.routed" && frame.invocation_id) {
      const kind = ITEM_OF_TARGET[payload.target];
      const held = byInvocation.get(frame.invocation_id);
      if (!kind) continue;
      const item = invocationItem(kind, frame);
      for (const earlier of held?.frames ?? []) consumed.add(earlier);
      if (!item.frames.includes(frame)) take(item, frame);
      item.action ??= payload.action;
      continue;
    }
    if (type === "action.refused" && frame.invocation_id) {
      const item = byInvocation.get(frame.invocation_id);
      if (item && item.kind !== "pending") finish(item, frame, "failed", payload);
      continue;
    }
    if (type.startsWith("tool.") && frame.invocation_id) {
      const item = invocationItem("tool", frame);
      if (!item.frames.includes(frame)) take(item, frame);
      if (type === "tool.started") item.tool = payload.tool;
      if (type === "tool.completed") finish(item, frame, "ok", payload);
      if (type === "tool.failed") finish(item, frame, "failed", payload);
      item.tool ??= payload.tool ?? item.action;
      continue;
    }
    if (type.startsWith("response.") && frame.invocation_id) {
      const item = invocationItem("response", frame);
      if (!item.frames.includes(frame)) take(item, frame);
      item.responseId ??= payload.response_id;
      if (type === "response.started" && payload.phase) item.phase = payload.phase;
      if (typeof payload.revision === "number") item.revisions = Math.max(item.revisions ?? 0, payload.revision);
      if (payload.snapshot !== undefined && payload.snapshot !== null) item.snapshot = payload.snapshot;
      if (type === "response.completed") {
        finish(item, frame, "ok", payload);
        item.phase = "final";
      } else if (type === "response.failed") finish(item, frame, "failed", payload);
      else if (type === "response.cancelled") finish(item, frame, "cancelled", payload);
      else item.phase ??= "partial";
      continue;
    }
    if (type.startsWith("child_agent.")) {
      const item =
        (frame.invocation_id && type !== "child_agent.message_sent" && type !== "child_agent.cancel_requested"
          ? invocationItem("child", frame)
          : undefined) ?? childByAgent.get(payload.agent_id ?? payload.target);
      if (!item) continue;
      if (!item.frames.includes(frame)) take(item, frame);
      if (type === "child_agent.spawn_requested") item.definition = payload.definition;
      if (type === "child_agent.spawned" && payload.agent_id) {
        item.childAgentId = payload.agent_id;
        childByAgent.set(payload.agent_id, item);
        item.state = "running";
      }
      if (type === "child_agent.final_received") {
        item.end = frame.at;
        item.state = "ok";
      }
      continue;
    }
    if (type.startsWith("error.")) {
      const item = open("error", frame.sequence, frame, { message: payload.message ?? payload.kind ?? payload.code });
      instant(item);
      item.state = "failed";
      continue;
    }
  }

  // An action whose routing never arrived, or routed somewhere no item
  // covers: its frames go back to being frames.
  for (const item of byInvocation.values()) {
    if (item.kind === "pending") for (const frame of item.frames) consumed.delete(frame);
  }

  // A partial response has no completion of its own: §27.3 ends it with its
  // last revision. Left "running" it would read as a response that hung.
  for (const item of items) {
    if (item.kind === "response" && item.phase === "partial" && item.end === undefined) {
      item.end = item.frames.at(-1)?.at;
      item.state = "ok";
    }
  }

  for (const item of items) {
    // A turn's own frames carry the id; everything else takes the turn that
    // was open on its agent when it started.
    if (!item.turnId) item.turnId = turnAt(model, item.agentId, item.start);
    item.title = titleOf(item, model);
    item.summary = summaryOf(item, model);
  }
  items.sort((a, b) => (a.start ?? 0) - (b.start ?? 0) || (a.frames[0]?.sequence ?? 0) - (b.frames[0]?.sequence ?? 0));
  const other = model.frames.filter((frame) => frame.frame !== "gap" && !consumed.has(frame));
  return { items, other };
}

/** The turn open on `agentId` at `at`, from the model's turns. */
function turnAt(model, agentId, at) {
  if (at === undefined) return null;
  for (const turn of model.turns.values()) {
    const owner = model.frames.find((frame) => frame.event_type === "session.turn_opened" && frame.turn_id === turn.id);
    if (owner && agentId && owner.agent_id !== agentId) continue;
    if (turn.opened !== undefined && at >= turn.opened && (turn.closed === undefined || at <= turn.closed)) {
      return turn.id;
    }
  }
  return null;
}

/** A card's title. */
function titleOf(item, model) {
  switch (item.kind) {
    case "turn":
      return item.index !== undefined ? `Turn ${item.index}` : "Turn";
    case "message":
      return item.sender ? `Message from ${item.sender}` : "Message";
    case "inference":
      return "Inference";
    case "tool":
      return item.tool ?? item.action ?? "Tool";
    case "response":
      if (item.phase === "partial") return "Partial response";
      return item.state === "running" ? "Responding…" : "Response";
    case "child": {
      const child = model.agents.get(item.childAgentId);
      return `Sub-agent · ${child?.definition ?? item.definition ?? "spawn"}`;
    }
    case "error":
      return "Error";
    default:
      return item.kind;
  }
}

/** A card's one line (§30.13.3). */
function summaryOf(item) {
  const facts = [];
  switch (item.kind) {
    case "turn":
      if (item.trigger) facts.push(String(item.trigger).replaceAll("_", " "));
      if (item.closedAs) facts.push(item.closedAs);
      break;
    case "message":
      if (item.priority) facts.push(item.priority);
      if (item.duplicate) facts.push("duplicate");
      break;
    case "inference": {
      if (item.model) facts.push(item.provider ? `${item.provider} · ${item.model}` : item.model);
      const usage = item.usage ?? {};
      const input = usage.input_tokens ?? usage.prompt_tokens;
      const output = usage.output_tokens ?? usage.completion_tokens;
      if (input !== undefined || output !== undefined) facts.push(`${input ?? "?"} → ${output ?? "?"} tokens`);
      if (typeof item.emittedActions === "number") {
        facts.push(`${item.emittedActions} action${item.emittedActions === 1 ? "" : "s"}`);
      }
      if (item.stopReason) facts.push(String(item.stopReason));
      break;
    }
    case "tool":
      facts.push(item.state === "running" ? "running" : item.state === "ok" ? "done" : item.state);
      break;
    case "response": {
      const said = snapshotText(item.snapshot);
      if (said) facts.push(`“${said}”`);
      if (item.revisions) facts.push(`${item.revisions} revision${item.revisions === 1 ? "" : "s"}`);
      break;
    }
    case "child":
      if (item.childAgentId) facts.push(short(item.childAgentId));
      break;
    case "error":
      if (item.message) facts.push(String(item.message));
      break;
    default:
      break;
  }
  return facts.join(" · ");
}

/** What a final snapshot says, read the way the observatory reads it. */
function snapshotText(snapshot) {
  const value = snapshot && typeof snapshot === "object" && snapshot.state === "inline" ? snapshot.value : snapshot;
  if (value && typeof value === "object" && typeof value.display_string === "string") return value.display_string;
  return "";
}

/**
 * The tree the third tab renders (§30.13.5):
 * session → agent → turn → pass → tool, response or child → frames,
 * with a sub-agent's own subtree under the spawn that created it.
 *
 * Built from `buildItems`, so the tree and the timeline cannot disagree
 * about what happened.
 */
export function buildTree(model, built = buildItems(model)) {
  const { items, other } = built;
  const node = (kind, label, extra = {}) => ({
    key: extra.key ?? `${kind}:${label}`,
    kind,
    family: extra.family ?? FAMILY_OF_ITEM[kind] ?? "agent",
    label,
    detail: extra.detail ?? "",
    agentId: extra.agentId ?? null,
    at: extra.at,
    frame: extra.frame,
    children: extra.children ?? [],
  });
  const frameNode = (frame) =>
    node("event", frame.event_type, {
      key: `frame:${frame.sequence}:${frame.event_type}`,
      family: frame.family ?? "gap",
      detail: `#${frame.sequence}`,
      agentId: frame.agent_id ?? null,
      at: frame.at,
      frame,
    });
  const itemDetail = (item) =>
    [item.summary, item.durationMs !== undefined ? elapsed(item.durationMs) : item.state === "running" ? "running" : ""]
      .filter(Boolean)
      .join(" · ");
  const spawned = new Set(items.filter((item) => item.kind === "child" && item.childAgentId).map((item) => item.childAgentId));

  const itemNode = (item) => {
    // Its own frames first, then what it decided, so a pass reads as
    // "started, finished, and this is what came of it".
    const children = item.frames.map(frameNode);
    if (item.kind === "inference") {
      for (const owned of items.filter((other) => other.passId && `inference:${other.passId}` === item.id)) {
        children.push(itemNode(owned));
      }
    }
    if (item.kind === "child" && item.childAgentId && model.agents.has(item.childAgentId)) {
      children.push(agentNode(item.childAgentId));
    }
    return node(item.kind, item.title, {
      key: item.id,
      family: item.family,
      detail: itemDetail(item),
      agentId: item.agentId,
      at: item.start,
      children,
    });
  };

  const agentNode = (agentId) => {
    const agent = model.agents.get(agentId);
    const mine = items.filter((item) => item.agentId === agentId && item.kind !== "turn");
    const owned = (item) => item.passId && mine.some((pass) => pass.id === `inference:${item.passId}`);
    const groups = new Map();
    for (const item of mine) {
      if (owned(item)) continue;
      const key = item.turnId ?? "";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    const others = new Map();
    for (const frame of other.filter((f) => f.agent_id === agentId)) {
      const key = frame.turn_id ?? turnAt(model, agentId, frame.at) ?? "";
      if (!others.has(key)) others.set(key, []);
      others.get(key).push(frame);
      if (!groups.has(key)) groups.set(key, []);
    }
    const turnNodes = [...groups.entries()].map(([turnId, grouped]) => {
      const turn = items.find((item) => item.kind === "turn" && item.turnId === turnId && turnId);
      const leftovers = others.get(turnId) ?? [];
      // The turn's own frames bracket what happened in it, so every frame
      // the stream sent is a leaf somewhere (§30.13.5).
      const [opened, ...closing] = turn?.frames ?? [];
      const children = [...(opened ? [frameNode(opened)] : []), ...grouped.map(itemNode)];
      if (leftovers.length) {
        children.push(
          node("other", "Other frames", {
            key: `other:${agentId}:${turnId}`,
            family: "gap",
            detail: `${leftovers.length}`,
            agentId,
            children: leftovers.map(frameNode),
          }),
        );
      }
      children.push(...closing.map(frameNode));
      return node("turn", turn ? turn.title : turnId ? `Turn ${short(turnId)}` : "Outside a turn", {
        key: `turn:${agentId}:${turnId}`,
        family: "session",
        detail: turn?.summary ?? "",
        agentId,
        at: turn?.start ?? grouped[0]?.start ?? leftovers[0]?.at,
        frame: turn?.frames[0],
        children,
      });
    });
    // A child with no spawn in the stream still belongs to its parent.
    const orphans = (agent?.children ?? []).filter((id) => !spawned.has(id)).map(agentNode);
    return node("agent", agentLabel(agent ?? { id: agentId }), {
      key: `agent:${agentId}`,
      family: "agent",
      detail: agent?.parent ? (model.agents.has(agent.parent) ? "sub-agent" : "parent not in this stream") : "root",
      agentId,
      children: [...turnNodes, ...orphans],
    });
  };

  return model.roots.filter((id) => !spawned.has(id)).map(agentNode);
}

/** The first eight characters of an id, which is what a reader compares. */
export function short(id) {
  return typeof id === "string" ? id.slice(0, 8) : "";
}
